/**
 * Allowed HTML tags and attributes for sanitizeHtml.
 * Covers the formatting used in alert notification templates.
 */
const ALLOWED_TAGS = new Set([
	"b",
	"i",
	"em",
	"strong",
	"u",
	"br",
	"p",
	"ul",
	"ol",
	"li",
	"span",
	"a",
]);

const ALLOWED_ATTRS = new Set(["href", "target", "rel"]);

/**
 * Recursively removes any elements or attributes not on the allowlist,
 * operating on an in-memory document so nothing is injected into the real DOM.
 */
function sanitizeNode(node: Element): void {
	// Walk children in reverse so removal doesn't affect indices
	const children = Array.from(node.childNodes);
	for (const child of children) {
		if (child.nodeType === Node.ELEMENT_NODE) {
			const el = child as Element;
			const tag = el.tagName.toLowerCase();
			if (!ALLOWED_TAGS.has(tag)) {
				// Replace disallowed element with its own text content
				node.replaceChild(
					document.createTextNode(el.textContent || ""),
					el
				);
			} else {
				// Strip disallowed attributes
				const attrs = Array.from(el.attributes);
				for (const attr of attrs) {
					if (!ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
						el.removeAttribute(attr.name);
					}
				}
				sanitizeNode(el);
			}
		}
	}
}

/**
 * Sanitizes an HTML string, allowing safe formatting tags (bold, italic,
 * links, line-breaks, etc.) but stripping any executable content such as
 * <script>, event-handler attributes, and dangerous elements like <img>.
 *
 * Use this wherever API-supplied content is rendered via v-html to prevent XSS.
 */
export function sanitizeHtml(dirty: string): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(dirty, "text/html");
	sanitizeNode(doc.body);
	return doc.body.innerHTML;
}

/**
 * Strips ALL HTML from a string, returning plain text only.
 *
 * Use this for fields that are never intended to contain markup
 * (e.g. toast notification messages).
 */
export function stripHtml(dirty: string): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(dirty, "text/html");
	return doc.body.textContent || "";
}
